import React from 'react';
import { useParams } from 'react-router-dom';


import { Box } from 'ui/box';

import { renderGoal } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-cell/goals-matrix-cell';
import { getTestId } from 'helpers/test-helpers';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';
import type { GoalSelectArg } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-sidebar/goals-sidebar';

interface GoalsSidebarItemProps {
  goal?: Goal;
  handleGoalSelect?: (arg: GoalSelectArg) => void;
  selectedGoal?: string | number;
  isNew?: boolean;
  disabled?: boolean;
}

const GoalsSidebarItem = ({
  goal,
  handleGoalSelect,
  selectedGoal,
  isNew,
  disabled,
}: GoalsSidebarItemProps): React.JSX.Element => {
  const { clientId, goalType, goalCategory, year } = useParams<{
    clientId: string;
    goalType: string;
    goalCategory: string;
    year: string;
  }>();
  const goalLink =
    goal &&
    `/admin/clients/${clientId}/goals/${year}/${goalType}/${goalCategory}/${goal.id}`;

  const baseClassName = `
    flex items-center text-left flex-wrap w-full max-w-full p-2.5 font-normal 
    text-gray-600 group overflow-hidden
    hover:text-navy-500 hover:font-medium 
    aria-pressed:text-navy-800 aria-pressed:bg-navy-50 aria-pressed:font-semibold 
    aria-pressed:border-l-4 aria-pressed:border-lime-400
  `;

  const newGoalClassName = `${baseClassName} cursor-default`;

  const existingGoalClassName = `
    ${baseClassName} 
    disabled:cursor-not-allowed disabled:opacity-50 
    disabled:hover:text-gray-600 disabled:hover:font-normal
  `;

  const newGoalTestId = getTestId({
    prefix: 'goal-sidebar-item',
    label: 'new',
    suffix: 'button',
  });

  const existingGoalTestId = getTestId({
    prefix: 'goal-sidebar-item',
    label: String(goal?.id) || '',
    suffix: 'button',
  });

  return isNew ? (
    <button
      className={newGoalClassName}
      aria-pressed={true}
      data-testid={newGoalTestId}
    >
      New...
    </button>
  ) : (
    <button
      className={existingGoalClassName}
      aria-pressed={selectedGoal === goal?.id}
      onClick={(e) =>
        handleGoalSelect?.({
          event: e,
          to: goalLink ?? '',
          id: goal?.id ?? '',
        })
      }
      disabled={disabled}
      data-testid={existingGoalTestId}
    >
      <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        {goal && renderGoal(goal)}
      </Box>
    </button>
  );
};

export default GoalsSidebarItem;
